/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { WidgetElements } from './widget-elements-base';
import { iam } from '../iam';

export class OktaWidgetElements extends WidgetElements {
  public getAccountRecoveryUsernameLabel(): HTMLElement {
    // widget's username label when account is locked
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type label[for=account-recovery-username]');
  }

  public getAccountRecoveryUsernameField(): HTMLInputElement {
    // widget's username field when account is locked
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLInputElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type #account-recovery-username');
  }

  public getEmailButton(): HTMLButtonElement {
    // widget's email button when account is locked
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLButtonElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type .email-button');
  }


  public getOldPasswordField(): HTMLInputElement {
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLInputElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type input[name="oldPassword"]');
  }

  public getNewPasswordField(): HTMLInputElement {
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLInputElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type input[name="newPassword"]');
  }

  public getConfirmPasswordField(): HTMLInputElement {
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLInputElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type input[name="confirmPassword"]');
  }

  public getHeaderBeacon(): HTMLElement {
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-header [data-type=beacon-container]');
  }

  public getMultiMfaDropdownFieldset(): HTMLElement {
    return document.getElementById('factorFieldset') as HTMLElement;
  }

  public getMultiMfaDropdown(): HTMLElement {
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-header [data-type=factor-types-dropdown]');
  }

  public getMfaEnrollmentFactorTile(factor: string): HTMLElement {
    return document.querySelector<HTMLElement>(`#okta-sign-in.auth-container .enroll-choices form [data-se=${factor}]`);
  }

  public getMfaFactor(): string {
    const factorIndicator = document.querySelector('#okta-sign-in.auth-container .mfa-verify form');
    return factorIndicator ? factorIndicator.getAttribute('data-se') : null;
  }

  public override getSubmitButton(): HTMLButtonElement {
    // widget's multi-purpose submit button (not used on *every* screen
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    let match = document.querySelector<HTMLButtonElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type #okta-signin-submit');
    if (!match) {
      match = document.querySelector<HTMLButtonElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type .o-form-button-bar input[type="submit"]');
    }
    return match;
  }

  public getFormHead(): HTMLElement {
    // widget's main title
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type .okta-form-title');
  }

  public getFormSubtitle(): HTMLElement {
    // widget's sometimes-used subtitle for displaying extra instruction/info like mfa enrolling info or phone number used for mfa
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type .okta-form-subtitle');
  }

  public getPasswordField(): HTMLInputElement {
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLInputElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type #okta-signin-password');
  }

  public getPhoneField(): HTMLInputElement {
    // widget's phone number field for enrolling in mfa
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLInputElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type input[name="phoneNumber"]');
  }

  public getPasscodeField(): HTMLInputElement {
    // widget's field for entering the passcode to enroll in mfa
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLInputElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type input[name="passCode"]');
  }

  public getAnswerField(): HTMLInputElement {
    // widget's field for mfa authentication
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLInputElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type input[name="answer"]');
  }

  public getSmsRequestButtons(): HTMLButtonElement[] {
    // widget's sms enrollment send buttons, of which there are 2 - a send and a re-send
    return Array.from(document.querySelectorAll<HTMLButtonElement>('#okta-sign-in.auth-container a.sms-request-button'));
  }

  public getCallRequestButtons(): HTMLButtonElement[] {
    // widget's call enrollment send buttons, of which there are 2 - a call and a redial
    return Array.from(document.querySelectorAll<HTMLButtonElement>('#okta-sign-in.auth-container a.call-request-button'));
  }

  public getUsernameLabel(): HTMLElement {
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type label[for=okta-signin-username]');
  }

  public getUsernameFieldset(): HTMLElement {
    const usernameLabel = this.getUsernameLabel();
    if (usernameLabel) {
      return usernameLabel.parentElement.parentElement;
    }
    return null;
  }

  public getPasswordLabel(): HTMLElement {
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type label[for=okta-signin-password]');
  }

  public getPasswordAutofilledField(): HTMLInputElement {
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLInputElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type #okta-signin-password:-webkit-autofill');
  }

  public getAuthFooter(): HTMLElement {
    // where Okta says 'Need help signing in?' below the sign in button
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type .auth-footer');
  }

  public getErrorContainer(): HTMLElement {
    // widget's element which gets populated with error messages
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type .o-form-error-container');
  }

  public getContextParent(): HTMLElement {
    // widget element used for watching for widget mode changes
    // get the parent of the element that contains the widget's context in the class attribute
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-content-inner');
  }

  public getContextFromWidget(): string {
    // examine widget dom and pull out the widget's context
    if ((this.getContextParent().lastChild as HTMLElement)?.className?.split(' ').length === 1) {
      const context = (this.getContextParent().lastChild as HTMLElement).className;
      if (iam.settings.debug) console.log('Found widget render context by examining okta dom: ' + context);
      return context;
    }
  }

  public getUsernameField(): HTMLInputElement {
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLInputElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type #okta-signin-username');
  }

  public getCountryDropdownField(): HTMLElement {
    // field is in the form, which means there will be 2 when transitioning, so always grab the last one
    return document.querySelector<HTMLElement>('#okta-sign-in.auth-container .auth-content-inner > div:last-of-type select[name=countryCode]').parentElement;
  }
}
